import type { PinpointFieldEnum } from '@/features/comment/models/pinpoint';
import { t } from '@/i18n';
import { AmountUnitLabelModifier, logger } from '@/utils';
import type { z } from 'zod';

export const pinpointLabelMap = (field: z.infer<typeof PinpointFieldEnum>) => {
  switch (field) {
    /// ////////////////////////////////////////////
    /// 共通フィールド
    /// ////////////////////////////////////////////
    case 'rs.project-number':
    case 'rs.skippedReview.project-number':
    case 'ss.project-number':
    case 'ks.investment.project-number':
    case 'ks.standard.project-number':
    case 'ks.local-government.project-number':
      return t('project-number');
    case 'rs.project-group':
    case 'rs.skippedReview.project-group':
    case 'ss.project-group':
    case 'ks.investment.project-group':
    case 'ks.standard.project-group':
    case 'ks.local-government.project-group':
      return t('project-group');
    case 'rs.sheet-type':
    case 'rs.skippedReview.sheet-type':
    case 'ss.sheet-type':
      return t('sheet-type');
    case 'rs.fiscal-year':
    case 'rs.skippedReview.fiscal-year':
    case 'ss.fiscal-year':
      return t('fiscal-year');
    case 'rs.project-category':
    case 'rs.skippedReview.project-category':
    case 'ss.project-category':
      return t('project-category');
    case 'rs.period':
    case 'rs.skippedReview.period':
    case 'ss.period':
    case 'ks.investment.period':
      return t('period');
    case 'rs.related-projects':
    case 'ss.related-projects':
    case 'ks.standard.related-projects':
      return t('related-projects');
    case 'rs.main-cost':
    case 'ss.main-cost':
      return t('main-cost');
    case 'rs.project-purpose':
    case 'ss.project-purpose':
    case 'ks.standard.project-purpose':
      return t('project-purpose');
    case 'rs.project-task':
    case 'ss.project-task':
    case 'ks.standard.project-task':
      return t('project-task');
    case 'rs.overview':
    case 'ss.overview':
    case 'ks.investment.overview':
    case 'ks.standard.overview':
      return t('project-overview');
    case 'rs.overview-url':
    case 'ss.overview-url':
    case 'ks.standard.overview-url':
      return t('overview-url');
    case 'rs.legal-bases':
    case 'ss.legal-bases':
    case 'ks.standard.legal-bases':
      return t('legal-bases');
    case 'rs.legal-bases-title':
    case 'ss.legal-bases-title':
    case 'ks.standard.legal-bases-title':
      return t('comment.width-title', {
        target: t('legal-bases'),
      });
    case 'rs.plan':
    case 'ss.plan':
    case 'ks.standard.plan':
      return t('plan');
    case 'rs.plan-title':
    case 'ss.plan-title':
    case 'ks.standard.plan-title':
      return t('comment.width-title', {
        target: t('plan'),
      });
    case 'rs.old-project-number':
    case 'rs.skippedReview.old-project-number':
    case 'ss.old-project-number':
    case 'ks.investment.old-project-number':
    case 'ks.standard.old-project-number':
    case 'ks.local-government.old-project-number':
      return t('old-project-number');
    case 'rs.project-order-number':
    case 'rs.skippedReview.project-order-number':
    case 'ss.project-order-number':
      return t('project-order-number');
    case 'rs.remark':
    case 'rs.skippedReview.remark':
    case 'ss.remark':
    case 'ks.investment.remark':
    case 'ks.standard.remark':
    case 'ks.local-government.remark':
      return t('remark');
    case 'rs.organization-in-charge':
    case 'rs.skippedReview.organization-in-charge':
    case 'ss.organization-in-charge':
    case 'ks.investment.organization-in-charge':
    case 'ks.standard.organization-in-charge':
    case 'ks.local-government.organization-in-charge':
      return t('organization-in-charge');
    case 'rs.people-in-charge':
    case 'rs.skippedReview.people-in-charge':
    case 'ss.people-in-charge':
    case 'ks.investment.people-in-charge':
    case 'ks.standard.people-in-charge':
    case 'ks.local-government.people-in-charge':
      return t('people-in-charge');
    case 'rs.business-manager':
    case 'rs.skippedReview.business-manager':
    case 'ss.business-manager':
    case 'ks.investment.business-manager':
    case 'ks.standard.business-manager':
    case 'ks.local-government.business-manager':
      return t('business-manager');
    case 'rs.other-organization-in-charge':
    case 'rs.skippedReview.other-organization-in-charge':
    case 'ss.other-organization-in-charge':
    case 'ks.investment.other-organization-in-charge':
    case 'ks.standard.other-organization-in-charge':
    case 'ks.local-government.other-organization-in-charge':
      return t('other-organization-in-charge');
    case 'rs.related-organizations':
    case 'rs.skippedReview.related-organizations':
    case 'ss.related-organizations':
    case 'ks.investment.related-organizations':
    case 'ks.standard.related-organizations':
    case 'ks.local-government.related-organizations':
      return t('related-organizations');
    case 'rs.revenue-source':
    case 'rs.skippedReview.revenue-source':
      return t('revenue-source');
    case 'rs.other-special-notes':
    case 'rs.skippedReview.other-special-notes':
    case 'ss.other-special-notes':
      return t('other-special-notes');
    case 'rs.related-material-image':
    case 'rs.skippedReview.related-material-image':
    case 'ss.related-material-image':
    case 'ks.investment.related-material-image':
    case 'ks.standard.related-material-image':
    case 'ks.local-government.related-material-image':
      return t('related-material');
    case 'rs.related-material-remark':
    case 'rs.skippedReview.related-material-remark':
    case 'ss.related-material-remark':
    case 'ks.investment.related-material-remark':
    case 'ks.standard.related-material-remark':
    case 'ks.local-government.related-material-remark':
      return t('remark');
    case 'rs.inspection-result':
      return t('comment.inspection-result');
    case 'ss.inspection-result':
    case 'ks.standard.inspection-result':
      return t('inspection-result');
    case 'rs.evaluation':
      return t('comment.evaluation');
    case 'ss.evaluation':
    case 'ks.standard.evaluation':
      return t('evaluation');
    case 'rs.direction-of-improvement':
      return t('comment.direction-of-improvement');
    case 'ks.standard.direction-of-improvement':
    case 'ss.direction-of-improvement':
      return t('direction-of-improvement');
    case 'rs.recurring-stats-table':
    case 'rs.skippedReview.recurring-stats-table':
    case 'ss.budget-amounts-table':
      return t('comment.pinpoint-budget-and-execution-amount-table');
    case 'rs.payment-group':
    case 'ss.payment-group-row':
    case 'ks.standard.payment-group-row':
      return t('payment-group');
    case 'rs.payment-contractor-row':
    case 'ss.payment-contractor-row':
      return t('payment-contractor');
    case 'rs.payment-amount-breakdown-title':
    case 'ss.payment-amount-breakdown-title': {
      return t('comment.width-title', {
        target: t('payment-amount-breakdown'),
      });
    }
    case 'ks.standard.payment-amount-breakdown-title': {
      return t('comment.width-title', {
        target: t(
          'payment-amount-breakdown-expenditure-from-each-block-in-previous-year',
        ),
      });
    }
    case 'rs.payment-amount-breakdown-row':
    case 'ks.standard.payment-amount-breakdown-row':
    case 'ss.payment-amount-breakdown-row': {
      return t('payment-amount-breakdown');
    }

    case 'rs.activity':
    case 'ss.activity':
    case 'ks.standard.activity':
    case 'ks.local-government.activity':
      return t('activity');
    case 'rs.connection-to-next-outcome':
    case 'ss.connection-to-next-outcome':
    case 'ks.standard.connection-to-next-outcome':
    case 'ks.local-government.connection-to-next-outcome':
      return t('connection-to-next-outcome');
    case 'rs.indicator-title':
    case 'ss.indicator-title':
    case 'ks.standard.indicator-title':
    case 'ks.local-government.indicator-title':
      return t('indicator-title');
    case 'rs.indicator-description':
    case 'ss.indicator-description':
    case 'ks.standard.indicator-description':
    case 'ks.local-government.indicator-description':
      return t('indicator-description');
    case 'rs.activity-and-result-target':
    case 'ss.activity-and-result-target':
    case 'ks.standard.activity-and-result-target':
    case 'ks.local-government.activity-and-result-target':
      return t('activity-and-result-target');
    case 'rs.achievement-for-qualitative-outcome':
    case 'ss.achievement-for-qualitative-outcome':
    case 'ks.standard.achievement-for-qualitative-outcome':
    case 'ks.local-government.achievement-for-qualitative-outcome':
      return t('achievement-for-qualitative-outcome');
    case 'rs.indicator-setting-reason':
    case 'ss.indicator-setting-reason':
    case 'ks.standard.indicator-setting-reason':
    case 'ks.local-government.indicator-setting-reason':
      return t('indicator-setting-reason');
    case 'rs.reason-for-setting-qualitative-outcome':
    case 'ss.reason-for-setting-qualitative-outcome':
    case 'ks.standard.reason-for-setting-qualitative-outcome':
    case 'ks.local-government.reason-for-setting-qualitative-outcome':
      return t('reason-for-setting-qualitative-outcome');
    case 'rs.reason-why-outcomes-cannot-be-multiple':
    case 'ss.reason-why-outcomes-cannot-be-multiple':
    case 'ks.standard.reason-why-outcomes-cannot-be-multiple':
    case 'ks.local-government.reason-why-outcomes-cannot-be-multiple':
      return t('reason-why-outcomes-cannot-be-multiple');
    case 'rs.payment-contractor-title':
    case 'ss.payment-contractor-title': {
      return t('comment.width-title', {
        target: t('payment-contractor-list-with-unit', {
          unit: AmountUnitLabelModifier.thousand,
        }),
      });
    }
    case 'rs.payment-title':
    case 'ss.payment-title':
      return t('comment.width-title', {
        target: t('payment-list-with-unit', {
          unit: AmountUnitLabelModifier.thousand,
        }),
      });
    /// ////////////////////////////////////////////
    /// RS固有のフィールド
    /// ////////////////////////////////////////////
    case 'rs.implementation-method':
      return t('implementation-method');
    case 'rs.subsidy-rate-and-so-on':
      return t('subsidy-rate-and-so-on');
    case 'rs.subsidy-rate-and-so-on-title':
      return t('comment.width-title', {
        target: t('subsidy-rate-and-so-on'),
      });

    case 'rs.budget-and-execution':
    case 'rs.skippedReview.budget-and-execution':
      return t('budget-and-execution-amount');
    case 'rs.increase-decrease-main-reason':
    case 'rs.skippedReview.increase-decrease-main-reason':
      return t('increase-decrease-main-reason');
    case 'rs.inspection-target':
      return t('comment.inspection-target');
    case 'rs.last-implemented-fiscal-year':
      return t('comment.last-implemented-fiscal-year');
    case 'rs.reason-of-target':
      return t('comment.reason-of-target');
    case 'rs.opinion':
      return t('comment.opinion');
    case 'rs.result-of-publishing':
      return t('comment.result-of-publishing');
    case 'rs.improvement-and-reflection-status':
      return t('improvement-and-reflection-status');
    case 'rs.reflected-amount':
      return t('reflected-amount');
    case 'rs.summary-of-publishing':
      return t('summary-of-publishing');
    case 'rs.summary-of-publishing-title':
      return t('comment.width-title', {
        target: t('summary-of-publishing'),
      });
    case 'rs.others-indicated-item':
      return t('others-indicated-item');
    case 'rs.process-and-opinion-by-administrative-review-team-opinion':
      return `${t('opinion')}（${t('process-and-opinion-by-administrative-review-team')}）`;
    case 'rs.process-and-opinion-by-administrative-review-team-details':
      return `${t('details')}（${t('process-and-opinion-by-administrative-review-team')}）`;
    case 'rs.reflection-status-based-on-opinion-details':
      return `${t('details')}（${t('reflection-status-based-on-opinion')}）`;
    case 'rs.recurring-stats-graph':
      return t('comment.pinpoint-budget-and-execution-amount-graph');
    case 'rs.activity-title':
    case 'ss.activity-title':
    case 'ks.standard.activity-title':
    case 'ks.local-government.activity-title':
      return t('comment.width-title', {
        target: t('activity'),
      });
    case 'rs.activities-and-outcomes-title':
    case 'ss.activities-and-outcomes-title':
    case 'ks.standard.activities-and-outcomes-title':
    case 'ks.local-government.activities-and-outcomes-title':
      return t('comment.width-title', {
        target: t('activities-and-outcomes'),
      });
    case 'rs.cabinet-decision-with-defined-indicators-name':
    case 'ks.standard.cabinet-decision-with-defined-indicators-name':
    case 'ks.local-government.cabinet-decision-with-defined-indicators-name':
      return t('comment.cabinet-decision-with-defined-indicators.name');
    case 'rs.cabinet-decision-with-defined-indicators-url':
    case 'ks.standard.cabinet-decision-with-defined-indicators-url':
    case 'ks.local-government.cabinet-decision-with-defined-indicators-url':
      return t('comment.cabinet-decision-with-defined-indicators.url');
    case 'rs.cabinet-decision-with-defined-indicators-relevant-part':
    case 'ks.standard.cabinet-decision-with-defined-indicators-relevant-part':
    case 'ks.local-government.cabinet-decision-with-defined-indicators-relevant-part':
      return t(
        'comment.cabinet-decision-with-defined-indicators.relevant-part',
      );
    case 'rs.others-indicated-item-title':
      return t('comment.width-title', {
        target: t('others-indicated-item'),
      });
    case 'rs.policy-title':
    case 'rs.skippedReview.policy-title':
      return t('comment.width-title', {
        target: t('policy'),
      });
    case 'rs.budget-and-execution-title':
    case 'rs.skippedReview.budget-and-execution-title':
      return t('comment.width-title', {
        target: t('budget-and-execution-title'),
      });
    case 'rs.policy':
    case 'rs.skippedReview.policy':
      return t('policy');
    /// ////////////////////////////////////////////
    /// RS対象外のフィールド
    /// ////////////////////////////////////////////
    case 'rs.skippedReview.reason':
      return t('skip-administrative-reform-team-evaluation-reason');
    case 'rs.skippedReview.reason-supplement':
      return t('skip-administrative-reform-team-evaluation-reason-supplement');
    case 'rs.skippedReview.expenditure-purpose-level-3-1':
      return t('expenditure-purpose-level-3-1');
    /// ////////////////////////////////////////////
    /// SS固有のフィールド
    /// ////////////////////////////////////////////
    case 'ss.independent-administrative-agency':
      return t('independent-administrative-agency');
    case 'ss.segment-unit-concept':
      return t('segment-unit-concept');
    case 'ss.budget-amounts-table-title':
      return t('comment.width-title', {
        target: t('budget-and-execution-title'),
      });
    /// ////////////////////////////////////////////
    /// KS固有のフィールド
    /// ////////////////////////////////////////////
    case 'ks.investment.fund-sheet-type':
    case 'ks.standard.fund-sheet-type':
    case 'ks.local-government.fund-sheet-type':
      return t('sheet-type');
    case 'ks.investment.fund-sheet-number':
    case 'ks.standard.fund-sheet-number':
    case 'ks.local-government.fund-sheet-number':
      return t('fund-sheet-number');
    case 'ks.investment.fund-sheet-branch-number':
    case 'ks.standard.fund-sheet-branch-number':
    case 'ks.local-government.fund-sheet-branch-number':
      return t('fund-sheet-branch-number');
    case 'ks.investment.corporate-name':
      return t('corporate-name');
    case 'ks.standard.operation-form':
    case 'ks.local-government.operation-form':
      return t('operation-form');
    case 'ks.standard.business-form':
    case 'ks.local-government.business-form':
      return t('business-form');
    case 'ks.standard.fund-necessity-type':
    case 'ks.local-government.fund-necessity-type':
      return t('fund-necessity-type');
    case 'ks.standard.fund-necessity-reasons-for-local-government':
    case 'ks.local-government.fund-necessity-reasons-for-local-government':
      return t('fund-necessity-reasons-for-local-government');
    /// ////////////////////////////////////////////
    ///  KS基金シート固有のフィールド
    /// ////////////////////////////////////////////
    case 'ks.standard.additional-organization-in-charge':
      return t('additional-organization-in-charge');
    case 'ks.standard.fundOwner':
      return t('fund-owner');
    case 'ks.standard.corporate-form':
      return t('corporate-form');
    case 'ks.standard.eligibility-inspection-method-and-reasons-for-selection':
      return t('eligibility-inspection-method-and-reasons-for-selection');
    case 'ks.standard.eligibility-inspection-results':
      return t('eligibility-inspection-results');
    case 'ks.standard.implementation-status-of-inspection':
      return t('implementation-status-of-inspection');
    case 'ks.standard.optional-corporate-form':
      return t('optional-corporate-form');
    case 'ks.standard.start-year':
      return t('start-fiscal-year-of-project');
    case 'ks.standard.end-year':
      return t('fund-scheduled-end-period');
    case 'ks.standard.end-year-supplementary-reasons':
      return t('comment.end-year.supplementary-reasons');
    case 'ks.standard.reason-for-changing-scheduled-end-period':
      return t('reason-for-changing-scheduled-end-period');
    case 'ks.standard.end-date-of-new-application':
      return t('end-date-of-new-application');
    case 'ks.standard.end-date-of-new-application-supplementary-reasons':
      return t('comment.end-date-of-new-application.supplementary-reasons');
    case 'ks.standard.reason-for-changing-end-date-of-new-application':
      return t('reason-for-changing-end-date-of-new-application');
    case 'ks.standard.composition-backgrounds-title':
    case 'ks.local-government.composition-backgrounds-title':
      return t('composition-backgrounds');
    case 'ks.standard.fund-budget-classification':
      return t('classification-of-the-fund');
    case 'ks.standard.recent-budget-measure-year':
      return t('latest-budget-year');
    case 'ks.standard.budget-reason-with-achievement-status-and-review-result':
      return t('budget-reason-and-outcome');
    case 'ks.standard.next-budget-measure-review-year':
      return t('next-budget-review-year');
    case 'ks.standard.national-treasury-return-background-title':
      return t('national-treasury-return-backgrounds');
    case 'ks.standard.effort-and-result-overview':
      return t('effort-and-result-overview-of-reviews-carried-out-in-the-past');
    case 'ks.standard.matter-of-act-for-normalization-of-grant':
      return t('matter-of-act-for-normalization-of-grant');
    case 'ks.standard.income-and-expenditure-table':
      return t('income-and-expenditure-table');
    case 'ks.standard.situation-of-divergence-in-execution-two-years-ago':
      return t('comment.situation-of-divergence-in-execution-two-years-ago');
    case 'ks.standard.situation-of-divergence-in-execution-one-years-ago':
      return t('comment.situation-of-divergence-in-execution-one-years-ago');
    case 'ks.standard.findings-of-external-experts':
      return t('findings-of-external-experts');
    case 'ks.standard.process-leading-to-the-findings-of-review-promotion-team':
      return t('process-leading-to-the-findings-of-review-promotion-team');
    case 'ks.standard.improvement-point-based-on-the-findings':
      return t('improvement-point-based-on-the-findings');
    case 'ks.standard.payment-title':
      return t('comment.width-title', {
        target: t(
          'payment-list-with-unit-expenditure-to-each-block-in-previous-year',
          {
            unit: AmountUnitLabelModifier.thousand,
          },
        ),
      });
    /// ////////////////////////////////////////////
    ///  KS出資状況表固有のフィールド
    /// ////////////////////////////////////////////
    case 'ks.investment.accumulated-amount-table':
      return t('accumulated-investment-amount');
    case 'ks.investment.accumulated-amount-from-the-government-table':
      return t(
        'accumulated-investment-amount-from-the-government-in-accounting-breakdown',
      );
    case 'ks.investment.fund-business-regarding':
      return t('fund-business-regarding-support-title');
    case 'ks.standard.fund-business-table':
      return t('fund-business');
    case 'ks.standard.ownership-ratio':
      return t('ownership-ratio');
    case 'ks.standard.ownership-ratio-numerator':
      return t('ownership-ratio-numerator');
    case 'ks.standard.ownership-ratio-denominator':
      return t('ownership-ratio-denominator');
    case 'ks.standard.contents-of-held-fund-amount-of-ownership-ratio':
      return t('contents-of-held-fund-amount-of-ownership-ratio');
    case 'ks.standard.contents-of-fund-project-costs-of-ownership-ratio':
      return t('contents-of-fund-project-costs-of-ownership-ratio');
    case 'ks.standard.concept-of-contents-of-each-term':
      return t('concept-of-contents-of-each-term');
    case 'ks.standard.basis-for-estimating-indicators-for-project-prospect':
      return t('basis-for-estimating-indicators-for-project-prospect');
    case 'ks.standard.indicators-for-project-prospect-and-recent-performance':
      return t('indicators-for-project-prospect-and-recent-performance');
    case 'ks.standard.has-ceased-operations':
      return t('has-ceased-operations');
    case 'ks.standard.has-no-operation-fund-detected':
      return t('has-no-operation-fund-detected');
    case 'ks.standard.has-fund-that-lost-purpose':
      return t('has-fund-that-lost-purpose');
    case 'ks.standard.has-exceed-1-significantly':
      return t('has-exceed-1-significantly');
    case 'ks.standard.has-deemed-unlikely-to-be-used':
      return t('has-deemed-unlikely-to-be-used');
    case 'ks.standard.reasons-for-selecting-absense-when-ownership-ratio-exceeds-1':
      return t('reason-for-selecting-absense-when-ownership-ratio-exceeds-1');
    case 'ks.standard.consideration-where-the-fund-are-unlikely-to-be-used':
      return t('consideration-where-the-fund-are-unlikely-to-be-used');
    case 'ks.standard.reason-for-leaving-the-fund-that-are-unlikely-to-be-used':
      return t('reason-for-leaving-the-fund-that-are-unlikely-to-be-used');
    /// ////////////////////////////////////////////
    ///  KS地方公共団体等基金固有のフィールド
    /// ////////////////////////////////////////////
    case 'ks.local-government.overview':
      return t('project-overview');
    case 'ks.local-government.fund-scheduled-end-period-for-local-government':
      return t('fund-scheduled-end-period-for-local-government');
    case 'ks.local-government.end-date-of-new-application-for-local-government':
      return t('end-date-of-new-application-for-local-government');
    case 'ks.local-government.status-of-response-by-ministries':
      return t('status-of-response-by-ministries');
    case 'ks.local-government.income-and-expenditure-table':
      return t('income-and-expenditure-table');
    case 'ks.local-government.fund-business-table':
      return t('fund-business-table');
    case 'ks.local-government.individual-table':
      return t('individual-table-with-unit', {
        unit: AmountUnitLabelModifier.thousand,
      });
    // 新規追加フィールド
    case 'rs.project-reference':
    case 'ss.project-reference':
    case 'ks.investment.project-reference':
    case 'ks.standard.project-reference':
    case 'ks.local-government.project-reference':
      return t('project-reference');
    case 'rs.legal-basis':
    case 'ss.legal-basis':
    case 'ks.investment.legal-basis':
    case 'ks.standard.legal-basis':
    case 'ks.local-government.legal-basis':
      return t('legal-basis');
    case 'rs.beneficiary-info':
    case 'ss.beneficiary-info':
    case 'ks.investment.beneficiary-info':
    case 'ks.standard.beneficiary-info':
    case 'ks.local-government.beneficiary-info':
      return t('beneficiary-info');
    case 'rs.related-project-for-other-ministry':
    case 'ss.related-project-for-other-ministry':
    case 'ks.investment.related-project-for-other-ministry':
    case 'ks.standard.related-project-for-other-ministry':
    case 'ks.local-government.related-project-for-other-ministry':
      return t('related-project-for-other-ministry');
    case 'rs.analysis-of-validity':
    case 'ss.analysis-of-validity':
    case 'ks.standard.analysis-of-validity':
      return t('analysis-of-validity');
    default: {
      const check: never = field;
      logger.error('意図せぬ値がpinpointに設定されています', check);
      return t('comment.deleted-pinpoint');
    }
  }
};
